import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import MediaPreview from 'components/MediaPreview';
import ActionLink from 'components/buttons/action-link/ActionLink';

import DeliverableMediaContextMenu from '../deliverable-media-context-menu/DeliverableMediaContextMenu';

import { downloadFileIndividually } from 'modules/campaigns/reducers/campaignDeliverables';

import { isValidAssetUrl } from 'utils/url';

import './deliverable-media-for-insights.scss';

import type { Deliverable, DeliverableAttachment } from 'types';
import type { RenderReplacementUploader, ReplacementUploaderProps } from './RenderReplacementUploader.type';

type OwnProps = {
  attachment: DeliverableAttachment,
  deliverable: Deliverable,
  isEditMode: boolean,
  isPost?: boolean,
  onEditModeToggled?: () => void,
  onDeleteAttachments?: (deliverableAttachments: Array<DeliverableAttachment>) => void,
  onReplaceAttachment?: (replacement: DeliverableAttachment, current: DeliverableAttachment) => void,
  onDeleteUrl?: () => void,
  onEditUrl?: () => void,
  renderReplacementUploader?: RenderReplacementUploader
};

type DispatchProps = {
  dispatchDownloadAttachment: (
    deliverable: Deliverable,
    deliverableAttachment: DeliverableAttachment,
    isNewDeliverable?: boolean) => void
};

type Props = OwnProps & DispatchProps;

type State = {
  isReplacing: boolean
};

class DeliverableMediaForInsights extends React.PureComponent<Props, State> {
  state: State = {
    isReplacing: false
  };

  private uploaderRef: React.RefObject<HTMLInputElement> = React.createRef();
  private ReplacementUploaderWrapper;

  constructor(props: Props) {
    super(props);

    const wrapperRenderer = props.renderReplacementUploader ?? (() => null);
    this.ReplacementUploaderWrapper = React.forwardRef(
      (wrapperProps: ReplacementUploaderProps, forwardedRef: React.ForwardedRef<HTMLInputElement>) => wrapperRenderer(wrapperProps, forwardedRef)
    );
  }

  componentDidUpdate(prevProps, prevState) {
    const { isEditMode } = this.props;
    const { isReplacing } = this.state;

    if (!isEditMode) {
      this.setReplacing(false);
    }

    if (isReplacing && this.uploaderRef.current && isReplacing !== prevState?.isReplacing) {
      this.uploaderRef.current.click();
    }
  }

  handleDownload = () => {
    const { attachment, deliverable, dispatchDownloadAttachment } = this.props;

    dispatchDownloadAttachment(deliverable, attachment, true);
  };

  handleCancelGallery = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  setReplacing = (isReplacing: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      isReplacing
    }));
  };

  handleReplacementFileUploaded = (tempAttachments: Array<DeliverableAttachment & { isInvalidImage?: boolean }>) => {
    if (tempAttachments && tempAttachments.length) {
      this.setReplacing(false);

      const { attachment, onReplaceAttachment } = this.props;

      if (onReplaceAttachment) {
        onReplaceAttachment(tempAttachments[0], attachment);
      }
    }
  };

  renderFileName(attachment: DeliverableAttachment, width: number): React.ReactNode {
    return (
      <span
        className="deliverable-media-for-insights__filename text-overflow-ellipsis"
        title={attachment.fileName}
        style={{ width: `${width}px` }}
      >
        {attachment.fileName}
      </span>
    );
  }

  renderFileLink(attachment: DeliverableAttachment): React.ReactNode {
    return (
      <ActionLink
        size="normal"
        className="text-overflow-ellipsis"
        title={attachment.fileName}
        link={attachment.fileName}
      >
        {attachment.fileName}
      </ActionLink>
    );
  }

  render(): React.ReactNode {
    const { isEditMode,
      attachment,
      deliverable,
      isPost,
      onEditModeToggled,
      onDeleteAttachments,
      onDeleteUrl,
      onEditUrl,
      renderReplacementUploader
    } = this.props;
    const { isReplacing } = this.state;

    const previewWidth = 183;
    const previewHeight = 183;
    const contextMenuWidth = 45;
    const isSupportedAsset = isValidAssetUrl(attachment.url);

    const contentClassNames = classnames('deliverable-media-for-insights', {
      'deliverable-media-for-insights--bordered': isSupportedAsset
    });

    if (isEditMode && isReplacing && renderReplacementUploader) {
      return (
        <this.ReplacementUploaderWrapper
          onUpdate={this.handleReplacementFileUploaded}
          ref={this.uploaderRef}
        />
      );
    }

    return (
      <div className={contentClassNames}>
        {isSupportedAsset && (
          <div className="deliverable-media-for-insights__preview">
            <MediaPreview
              key={`deliverable-media-attachment-preview-${attachment.id}`}
              attachment={{
                ...attachment,
                format: attachment.mediaType || attachment.assetType
              }}
              width={previewWidth}
              height={previewHeight}
              showPdfFileName={false}
            />
          </div>
        )}
        <div
          className="deliverable-media-for-insights__title"
          onClick={this.handleCancelGallery}
        >
          {isSupportedAsset && this.renderFileName(attachment, previewWidth - contextMenuWidth)}
          {!isSupportedAsset && this.renderFileLink(attachment)}
          <DeliverableMediaContextMenu
            key={`deliverable-media-for-insights__context-menu--${deliverable.id}`}
            onUploadModeToggled={onEditModeToggled}
            isBulk={false}
            deliverable={deliverable}
            isEditMode={isEditMode}
            attachments={[attachment]}
            isPost={isPost}
            onDownloadAttachments={this.handleDownload}
            onDeleteAttachments={onDeleteAttachments}
            onReplaceAttachment={() => this.setReplacing(true)}
            isInsights
            onDeleteUrl={onDeleteUrl}
            onEditUrl={onEditUrl}
          />
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDownloadAttachment: (deliverable, attachment, isNewDeliverable) =>
    dispatch(downloadFileIndividually(deliverable, attachment, isNewDeliverable))
});

export default connect(null, mapDispatchToProps)(DeliverableMediaForInsights);
